import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const UAESection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeEmirate, setActiveEmirate] = useState(() => localStorage.getItem('selectedEmirate') || null);

  useEffect(() => {
    const stored = localStorage.getItem('selectedEmirate');
    setActiveEmirate(stored);
  }, [location]);

  const uaeFeatures = [
    {
      id: 1,
      icon: 'Shield',
      title: 'RERA Compliance Intelligence',
      description: 'Full integration with Dubai Land Department (DLD) and RERA regulations. Instant compliance checks, escrow verification, and developer registration status for every off-plan and ready property.',
      route: '/rera-compliance-intelligence'
    },
    {
      id: 2,
      icon: 'TrendingUp',
      title: 'Off-Plan Analytics & ROI',
      description: 'Deep analysis of off-plan projects across all UAE developers. Track payment plans, handover timelines, projected ROI, and compare against secondary market alternatives in real time.',
      route: '/off-plan-analytics-roi'
    },
    {
      id: 3,
      icon: 'Globe',
      title: 'Expat Buyer Guidance',
      description: 'Tailored insights for international buyers — freehold vs leasehold zones, visa-linked property thresholds (AED 750K & AED 2M), ownership restrictions, and residency eligibility rules.',
      route: '/expat-buyer-guidance'
    },
    {
      id: 4,
      icon: 'Calculator',
      title: 'UAE Mortgage & Finance Tools',
      description: 'UAE-specific mortgage calculators with local bank rates, LTV ratios for expats vs UAE nationals, DLD transfer fee estimates (4%), and total cost-of-ownership breakdowns in AED.',
      route: '/uae-mortgage-finance'
    },
    {
      id: 5,
      icon: 'Building2',
      title: 'Dubai & Abu Dhabi Deep Dive',
      description: "Granular data for Dubai's top communities (Downtown, Marina, JVC, Palm Jumeirah, Business Bay) and Abu Dhabi's prime areas (Yas Island, Saadiyat, Al Reem) with price-per-sqft benchmarks.",
      route: '/dubai-abu-dhabi-deepdive'
    },
    {
      id: 6,
      icon: 'Globe',
      title: 'All 7 Emirates Coverage',
      description: 'Complete property market data across all seven UAE emirates — Dubai, Abu Dhabi, Sharjah, Ajman, Ras Al Khaimah, Fujairah, and Umm Al Quwain — with emirate-specific insights.',
      route: '/all-7-emirates'
    },
    {
      id: 7,
      icon: 'BarChart2',
      title: 'UAE Market Intelligence Reports',
      description: 'AI-powered monthly market intelligence reports analyzing emirate trends, pricing forecasts, RERA regulatory updates, off-plan launches, and expat buying patterns across all 7 emirates.',
      route: '/uae-market-intelligence-reports'
    }
  ];

  const uaeStats = [
    { value: 'AED 1.2M', label: 'Dubai Avg Price (2025)' },
    { value: '7', label: 'Emirates Covered' },
    { value: '+22%', label: 'Dubai Growth 2024' },
    { value: '100%', label: 'RERA Compliant' }
  ];

  const emirates = [
    { name: 'Dubai', flag: '🏙️', highlight: 'Global hub, +22% YoY' },
    { name: 'Abu Dhabi', flag: '🕌', highlight: 'Capital, luxury & culture' },
    { name: 'Sharjah', flag: '🏛️', highlight: 'Affordable, family-friendly' },
    { name: 'Ajman', flag: '🌊', highlight: 'Budget buys, high yield' },
    { name: 'Ras Al Khaimah', flag: '⛰️', highlight: 'Tourism & resort growth' },
    { name: 'Fujairah', flag: '🌿', highlight: 'East coast, scenic living' },
    { name: 'Umm Al Quwain', flag: '🦅', highlight: 'Emerging, low-cost entry' }
  ];

  const handleEmirateClick = (emirateName) => {
    localStorage.setItem('selectedEmirate', emirateName);
    setActiveEmirate(emirateName);
    navigate(`/search-results?location=${encodeURIComponent(emirateName?.toLowerCase())}&region=uae&mode=buy`);
  };

  return (
    <section id="uae" className="py-12 md:py-20 lg:py-28 relative overflow-hidden" style={{ background: 'transparent' }}>
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.07) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.06) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm md:text-base font-medium mb-4" style={{
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            color: '#00D4FF'
          }}>
            <span>🇦🇪</span>
            <span>UAE MARKET — RERA COMPLIANT</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 md:mb-6" style={{
            fontFamily: "'Orbitron', sans-serif"
          }}>
            <span style={{ color: '#00D4FF', textShadow: '0 0 30px rgba(0, 212, 255, 0.4)' }}>Built Specifically for</span>
            <span style={{ color: '#E8F4FF' }}> UAE Property Market</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto" style={{ color: '#6B9EC4' }}>
            ListingLens delivers AI-powered property intelligence tailored to the UAE — RERA compliance, off-plan analytics, expat guidance, and full coverage across all 7 emirates. Powered by DLD, RERA, and live market data.
          </p>
        </div>

        {/* UAE Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {uaeStats?.map((stat, idx) => (
            <div key={idx} className="text-center p-4 md:p-6 rounded-xl" style={{
              background: 'rgba(10, 22, 40, 0.8)',
              border: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <div className="text-2xl md:text-3xl font-bold mb-1" style={{
                fontFamily: "'JetBrains Mono', monospace",
                color: '#00D4FF',
                textShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
              }}>{stat?.value}</div>
              <div className="text-xs md:text-sm" style={{ color: '#6B9EC4' }}>{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* All 7 Emirates — clickable buttons */}
        <div className="mb-12 md:mb-16 p-6 md:p-8 rounded-2xl" style={{
          background: 'rgba(10, 22, 40, 0.8)',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <div className="flex items-center gap-3 mb-6">
            <Icon name="MapPin" size={22} color="#00D4FF" />
            <h3 className="text-lg md:text-xl font-bold" style={{ color: '#E8F4FF' }}>All 7 Emirates — Complete Coverage</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {emirates?.map((emirate, idx) => {
              const isActive = activeEmirate === emirate?.name;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleEmirateClick(emirate?.name)}
                  className="p-4 rounded-xl flex items-start gap-3 text-left w-full transition-all duration-200 relative cursor-pointer"
                  style={isActive ? {
                    background: 'rgba(0, 212, 255, 0.15)',
                    border: '1px solid rgba(0, 212, 255, 0.7)',
                    boxShadow: '0 0 16px rgba(0, 212, 255, 0.35), inset 0 0 8px rgba(0, 212, 255, 0.08)'
                  } : {
                    background: 'rgba(0, 212, 255, 0.05)',
                    border: '1px solid rgba(0, 212, 255, 0.15)'
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(0, 212, 255, 0.12)';
                      e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.5)';
                      e.currentTarget.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.25)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                      e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {isActive && (
                    <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full" style={{
                      background: '#00D4FF',
                      boxShadow: '0 0 6px rgba(0, 212, 255, 0.8)'
                    }} />
                  )}
                  <span className="text-2xl flex-shrink-0">{emirate?.flag}</span>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: '#E8F4FF' }}>{emirate?.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: '#00D4FF' }}>{emirate?.highlight}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {uaeFeatures?.map((feature) => (
            <button
              key={feature?.id}
              type="button"
              onClick={() => navigate(feature?.route)}
              className="p-6 md:p-8 rounded-xl transition-all duration-300 text-left w-full cursor-pointer"
              style={{
                background: 'rgba(10, 22, 40, 0.8)',
                border: '1px solid rgba(0, 212, 255, 0.15)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.45)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 md:mb-6" style={{
                background: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid rgba(0, 212, 255, 0.25)'
              }}>
                <Icon name={feature?.icon} size={24} color="#00D4FF" />
              </div>
              <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3" style={{ color: '#E8F4FF' }}>
                {feature?.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: '#6B9EC4' }}>
                {feature?.description}
              </p>
              <div className="mt-4 flex items-center gap-1 text-xs font-medium" style={{ color: '#00D4FF' }}>
                <span>Explore</span>
                <Icon name="ArrowRight" size={12} color="#00D4FF" />
              </div>
            </button>
          ))}
        </div>

        {/* UAE Coverage Footer with CTA */}
        <div className="text-center mt-12 md:mt-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Icon name="MapPin" size={18} color="#00D4FF" />
            <span className="text-sm md:text-base font-medium" style={{ color: '#6B9EC4' }}>100% Coverage Across UAE</span>
          </div>
          <div className="inline-flex flex-wrap items-center justify-center gap-2 mb-3">
            {['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']?.map((emirateName) => {
              const isActive = activeEmirate === emirateName;
              return (
                <button
                  key={emirateName}
                  type="button"
                  onClick={() => handleEmirateClick(emirateName)}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 relative"
                  style={isActive ? {
                    background: 'rgba(0, 212, 255, 0.25)',
                    border: '1px solid rgba(0, 212, 255, 0.8)',
                    color: '#00D4FF',
                    boxShadow: '0 0 16px rgba(0, 212, 255, 0.45), inset 0 0 8px rgba(0, 212, 255, 0.1)'
                  } : {
                    background: 'rgba(0, 212, 255, 0.1)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    color: '#00D4FF'
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
                      e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.6)';
                      e.currentTarget.style.boxShadow = '0 0 12px rgba(0, 212, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                      e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.3)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {isActive && (
                    <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full" style={{
                      background: '#00D4FF',
                      boxShadow: '0 0 6px rgba(0, 212, 255, 0.8)'
                    }} />
                  )}
                  {emirateName}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs" style={{ color: '#4A7A9B' }}>Covering Dubai · Abu Dhabi · Sharjah · Ajman · Ras Al Khaimah · Fujairah · Umm Al Quwain</p>
          <div className="inline-flex flex-wrap items-center justify-center gap-2 px-6 py-3 rounded-xl mt-6" style={{
            background: 'rgba(0, 212, 255, 0.06)',
            border: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <Icon name="Shield" size={18} color="#00D4FF" />
            <span className="text-sm md:text-base font-medium" style={{ color: '#6B9EC4' }}>
              RERA Compliant: <span style={{ color: '#00D4FF' }}>Dubai · Abu Dhabi · Sharjah · Ajman · Ras Al Khaimah · Fujairah · Umm Al Quwain</span>
            </span>
          </div>
          <p className="mt-3 text-xs" style={{ color: '#4A7A9B' }}>Full DLD integration · Off-plan & ready properties · Expat & UAE national guidance</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/search-results?region=uae&mode=buy')}
              className="px-8 py-3 rounded-xl font-semibold text-sm md:text-base cursor-pointer transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#000000',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 212, 255, 0.6)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Explore UAE Properties →
            </button>
            <button
              type="button"
              onClick={() => navigate('/list-your-property-landing')}
              className="px-8 py-3 rounded-xl font-semibold text-sm md:text-base cursor-pointer transition-all duration-200"
              style={{
                background: 'transparent',
                border: '2px solid rgba(0, 255, 136, 0.6)',
                color: '#00FF88',
                boxShadow: '0 0 16px rgba(0, 255, 136, 0.2)'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(0, 255, 136, 0.1)';
                e.currentTarget.style.boxShadow = '0 0 24px rgba(0, 255, 136, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(0, 255, 136, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Sell Your Property →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UAESection;